import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setField } from 'actions/nodeSettings';
import { Settings, SettingsTextField } from 'components/SettingsNew';
import ButtonNew from 'components/inputs/ButtonNew';
import './NodeSettings.scss';
import NodeSettingsBasic from './NodeSettingsBasic';
import NodeSettingsAdvanced from './NodeSettingsAdvanced';
import Popover from '../../Popover';
import classnames from 'classnames';
import { useParams, useHistory } from 'react-router';
import { startEdit, cancelEdit, finishEdit } from 'actions/nodeSettings';
import { getNode } from 'constants/nodes';
import { deleteConnectionsTo } from 'constants/connections';
import SettingsModal from 'components/SettingsModal';
import { saveMatrix, resetMatrix } from 'actions/matrices';
import { deleteNode } from 'constants/matrices';
import ModalFooter from '../../ModalFooter';
import { useCanEdit, useIsInUse } from 'hooks/matrices';
import { writePermissions } from 'constants/auth';

const DeletePopoverContent = ({
  text,
  primaryAction,
  primaryActionLabel,
  secondaryAction,
  secondaryActionLabel,
}) => {
  return (
    <div className="MatrixSettings-DeletePopoverContent">
      <div className="Popover-section">{text}</div>

      <div className="MatrixSettings-DeletePopoverContent-buttons-container">
        <ButtonNew
          buttonClasses="secondary-button-dark-small"
          onClick={secondaryAction}
          label={secondaryActionLabel}
        />

        <ButtonNew
          label={primaryActionLabel}
          onClick={primaryAction}
          buttonClasses="destructive-button-small"
        />
      </div>
    </div>
  );
};

const MatrixFooter = () => {
  return (
    <div>
      Not sure which settings to choose? Learn more about how the MPG matrix
      works in our{' '}
      <a
        href="https://docs.google.com/document/d/1HGGWkx9PZrsdBR3CNIeZvDfOc0IhHvV7jFi56v4PGjg/edit"
        target="_blank"
      >
        FAQs
      </a>
    </div>
  );
};

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

// Node settings
const NodeSettings = ({
  matrix,
  readOnly,
  saveUrl,
  cancelUrl,
  setShowPublicWarning,
  publicWarningDismissed,
  showPublicWarning,
}) => {
  const dispatch = useDispatch();

  const prevShowPublicWarning = usePrevious(showPublicWarning);

  const { isAdmin } = useSelector(state => state.auth.userData);
  const { user } = useSelector(state => state.auth);
  const { user_id } = user?.claims ?? { user_id: null };
  const canEdit = useCanEdit();

  const isAnonymous = useMemo(() => {
    return (
      !user &&
      matrix?.createdBy === 'anonymous user' &&
      Object.keys(matrix?.roles)?.length === 0
    );
  }, [matrix, user]);

  const hasEditAccess = useMemo(() => {
    return isAnonymous || writePermissions.includes(matrix?.roles?.[user_id]);
  }, [matrix, user_id, isAnonymous]);
  const isInUse = useIsInUse({ id: matrix?.id, hasEditAccess });

  const matrixIsInUse = useMemo(() => {
    return (
      hasEditAccess &&
      !!isInUse &&
      isInUse?.user !== user_id &&
      isInUse?.user !== null
    );
  }, [user_id, isInUse, hasEditAccess]);

  const editable = useMemo(() => {
    // TODO This will require more nuance because they need to edit the matrix to choose this
    const isViewOnly = matrix?.isViewOnly;
    return isAnonymous || (canEdit(matrix) && !matrixIsInUse && !isViewOnly);
  }, [matrixIsInUse, matrix, canEdit, isAnonymous]);

  const noEdit = useMemo(() => {
    return matrixIsInUse || !hasEditAccess || !editable;
  }, [matrixIsInUse, hasEditAccess, editable]);

  const { validationMessages } = useSelector(state => state.matrixSettings);

  const [settingsState, setSettingsState] = useState('basic_info');

  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);

  let [isEditing, setIsEditing] = useState(false);

  const deleteRef = useRef(null);

  const node = getNode(matrix, useParams().node);
  const history = useHistory();

  const editMatrix = useSelector(state => state.nodeSettings.matrix);
  const editNodeId = useSelector(state => state.nodeSettings.nodeId);

  const { valid: nodeIsValid } = useSelector(state => state.nodeSettings);

  let editNode = null;
  if (editMatrix.nodes) {
    editNode = editMatrix.nodes[editNodeId];
  }

  useEffect(() => {
    dispatch(startEdit(matrix, node?.id));
  }, [dispatch, matrix, node]);

  const onSave = useCallback(() => {
    dispatch(finishEdit(editMatrix));
    if (nodeIsValid) {
      dispatch(saveMatrix(editMatrix.id));
    }
    history.push(saveUrl);
  }, [editMatrix, dispatch, history, nodeIsValid, saveUrl]);

  const onDeleteNode = useCallback(() => {
    let newMatrix = deleteNode(matrix, editNodeId);
    newMatrix = deleteConnectionsTo(newMatrix, editNodeId);
    dispatch(finishEdit(newMatrix));
    dispatch(saveMatrix(newMatrix.id));
    // Uses replace because push would create a state where
    // pressing back throws an error.
    history.replace(saveUrl);
  }, [dispatch, history, editNodeId, saveUrl, matrix]);

  const onExit = useCallback(() => {
    dispatch(cancelEdit());
    history.push(cancelUrl);
  }, [dispatch, history, cancelUrl]);

  const menuOptions = [
    {
      label: 'Basic info',
      value: 'basic_info',
      content: (
        <NodeSettingsBasic
          matrix={editMatrix}
          node={editNode}
          readOnly={readOnly}
          isEditing={isEditing}
          onSave={onSave}
        />
      ),
    },
    {
      label: 'Advanced',
      value: 'advanced',
      content: (
        <NodeSettingsAdvanced
          matrix={matrix}
          node={editNode}
          readOnly={readOnly}
          isEditing={isEditing}
        />
      ),
    },
  ];

  const tertiaryAction = useCallback(() => {
    if (settingsState === 'basic_info') {
      setSettingsState('advanced');
    } else {
      setSettingsState('basic_info');
    }
  }, [settingsState]);

  const tertiaryActionLabel = useMemo(() => {
    if (settingsState === 'basic_info') {
      return 'Advanced settings';
    } else {
      return 'Go back';
    }
  }, [settingsState]);

  const matrixIsPublic = useMemo(() => {
    return matrix?.isPublic ?? false;
  }, [matrix]);

  useEffect(() => {
    if (
      prevShowPublicWarning &&
      !showPublicWarning &&
      (!noEdit || isAdmin) &&
      !matrixIsInUse
    ) {
      setIsEditing(true);
    }
  }, [
    showPublicWarning,
    prevShowPublicWarning,
    noEdit,
    isAdmin,
    matrixIsInUse,
  ]);

  if (!editNode) {
    return null;
  }
  return (
    <SettingsModal
      className="NodeSettingsView"
      onExit={noEdit ? onExit : onSave}
    >
      <div className="NodeSettings tour-node-settings">
        <div className="NodeSettings-main">
          <div className="NodeSettings-row">
            {isEditing ? (
              <SettingsTextField
                onChange={value => dispatch(setField('name', value))}
                placeholder="Node name"
                validationMessage={validationMessages.name}
                value={editNode.name}
                isEditing={isEditing}
              />
            ) : (
              <div className="NodeSettings-name">{editNode.name}</div>
            )}

            {/* Edit */}
            {!noEdit || publicWarningDismissed ? (
              <div className="NodeSettings-button-container">
                <ButtonNew
                  buttonClasses={classnames(
                    'settings-action-button NodeSettings-edit-button',
                    { active: isEditing }
                  )}
                  onClick={() => {
                    if (
                      matrixIsPublic &&
                      !publicWarningDismissed &&
                      !isEditing
                    ) {
                      setShowPublicWarning(true);
                    } else {
                      dispatch(resetMatrix(true));
                      setIsEditing(!isEditing);
                    }
                  }}
                  iconLeft="pencil"
                  label={isEditing ? 'Editing node' : 'Edit node'}
                />
              </div>
            ) : (
              <div className="NodeSettings-button-container">
                <ButtonNew
                  buttonClasses={classnames(
                    'settings-action-button NodeSettings-edit-button',
                    { active: isEditing }
                  )}
                  onClick={() => {
                    if (!isEditing) {
                      setShowPublicWarning(true);
                    }
                  }}
                  iconLeft="pencil"
                  label={'Edit node'}
                />
              </div>
            )}

            {/* Delete */}
            {isEditing ? (
              <div ref={deleteRef} className="NodeSettings-button-container">
                <ButtonNew
                  buttonClasses="settings-action-button NodeSettings-delete-button"
                  onClick={() => setDeletePopoverOpen(!deletePopoverOpen)}
                  iconLeft="trash"
                  label="Delete node"
                />
              </div>
            ) : null}
          </div>
          <Settings
            menuOptions={menuOptions}
            setMenuOption={setSettingsState}
            activeMenuOption={settingsState}
            header="Edit Node"
            footer={<MatrixFooter />}
          />
        </div>

        {/* Footer */}
        <ModalFooter
          secondaryAction={() => {
            dispatch(cancelEdit());
            history.push(cancelUrl);
          }}
          secondaryActionLabel={'Cancel'}
          primaryAction={onSave}
          primaryActionDisabled={!nodeIsValid || (noEdit && !isEditing)}
          primaryActionLabel={'OK'}
          tertiaryAction={tertiaryAction}
          tertiaryActionLabel={tertiaryActionLabel}
        />

        {/* Delete popover */}
        <Popover
          title={'Delete node'}
          content={
            <DeletePopoverContent
              text={
                'Deleting this node will impact the rest of the matrix. You may undo this action in the modifications panel.'
              }
              primaryAction={onDeleteNode}
              primaryActionLabel={'Delete'}
              secondaryAction={() => setDeletePopoverOpen(false)}
              secondaryActionLabel={'Cancel'}
            />
          }
          isOpen={deletePopoverOpen}
          parentRef={deleteRef.current}
        />
      </div>
    </SettingsModal>
  );
};

export default NodeSettings;
